import {
  IngredientDataType,
  IngredientType
} from "../../../config/enums/IngredientType.enum";
import {
  EnvironmentalContext,
  PackageFlags,
  SupplierDataShape,
  SupplierIngredientDetails
} from "./supplierRegister.types";

const ENVIRONMENTAL_DATA_TYPES = [
  IngredientDataType.CALCULATED_LCA,
  IngredientDataType.GENERIC
];

export const shouldEnableEnvironmentalData = (
  details?: Pick<SupplierIngredientDetails, "data_type" | "ingredient_type">
) => {
  if (!details) return false;

  if (details.data_type === IngredientDataType.CALCULATED_LCA) return true;
  if (details.data_type === IngredientDataType.GENERIC) return true;

  return details.ingredient_type !== "";
};

export const resolveIngredientDataType = ({
  salesDataType,
  supplierDataType,
  fallback = IngredientDataType.USER
}: {
  salesDataType?: string;
  supplierDataType?: string;
  fallback?: string;
}) => {
  if (
    supplierDataType &&
    ENVIRONMENTAL_DATA_TYPES.includes(supplierDataType as IngredientDataType)
  ) {
    return supplierDataType;
  }

  if (salesDataType && ENVIRONMENTAL_DATA_TYPES.includes(salesDataType as IngredientDataType)) {
    return salesDataType;
  }

  return salesDataType || supplierDataType || fallback;
};

export const derivePackageFlags = (
  dataType: string,
  ingredientType: string
): PackageFlags => {
  const flags: PackageFlags = {
    processIngredient: false,
    produceIngredient: false,
    enableIngredient: false,
    lcaIngredient: false
  };

  const isGeneric = dataType === IngredientDataType.GENERIC;
  const isLCA = dataType === IngredientDataType.CALCULATED_LCA;

  if (ingredientType === IngredientType.PROCESSED && isGeneric) {
    flags.processIngredient = true;
  } else if (ingredientType === IngredientType.RAW && isGeneric) {
    flags.produceIngredient = true;
    flags.enableIngredient = true;
  } else if (
    (ingredientType === IngredientType.RAW || ingredientType === IngredientType.PROCESSED) &&
    isLCA
  ) {
    flags.lcaIngredient = true;
  }

  return flags;
};

export const buildSupplierIngredientDetails = (
  formData: Record<string, unknown>,
  context: EnvironmentalContext
) => {
  return Object.assign(
    {
      ingredient_id: context.ingredientId,
      unit_type: context.defaultType,
      unit_size: formData.unit_size,
      ean_bar_code: formData.ean_bar_code,
      product_id: formData.product_id || ""
    },
    context.viewEnvironmentalData
      ? {
          data_type: context.dataType,
          ingredient_type: context.ingredientType,
          countryOrigin: context.country,
          specifyIngredient: context.specifyIngredientValue,
          produceType: context.produceType,
          produceCultivar: formData.produceCultivar,
          farmingType: context.farmingType,
          fieldType: context.fieldType
        }
      : {
          data_type: "",
          ingredient_type: "",
          countryOrigin: 0,
          specifyIngredient: "",
          produceType: "",
          produceCultivar: "",
          farmingType: "",
          fieldType: ""
        }
  );
};

export const getIngredientAndSupplierId = (
  supplierData: SupplierDataShape["supplier_data"],
  ingredientId?: string
) => {
  const supplierProductId =
    supplierData?.supProductIds || supplierData?.productIds || supplierData?.supplierProductIds;
  const supplierIngredientData = (
    (supplierData?.supIngredientIds ||
      supplierData?.ingredientIds ||
      supplierData?.supplierIngredientIds) as string[] | undefined
  ) ?? [];

  const newSupplierProductIds = supplierProductId;
  const newSupplierIngIds = ingredientId && supplierIngredientData.includes(ingredientId)
    ? supplierIngredientData
    : [...supplierIngredientData, ...(ingredientId ? [ingredientId] : [])];

  return {
    newSupplierProductIds,
    newSupplierIngIds
  };
};

export const getSupplierId = (supplierData: SupplierDataShape) =>
  supplierData?.supplier_data?.supplier_id ||
  supplierData?.supplier_data?._id ||
  supplierData?.supplier_id;

export const getIngredientId = (ingredientData?: Record<string, unknown>) => {
  const data = ingredientData as Record<string, any> | undefined;
  return (
    data?.ingredient_data?.ingredient_id ||
    data?.ingredient_data?._id ||
    data?.ingredient_id
  );
};
